import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { ShieldCheck } from 'lucide-react'

import { Container } from '@/components/generals/container'
import { BrandMark } from '@/components/generals/brand'

import { HUBS } from '@/constants/hubs'
import { siteConfig } from '@/constants/site'

export const SiteFooter: FC = (): ReactNode => {
    const year = new Date().getFullYear()

    return (
        <footer className='mt-auto border-t border-border bg-secondary/40'>
            <Container className='py-14'>
                <div className='grid gap-10 md:grid-cols-4'>
                    <div className='md:col-span-2'>
                        <div className='flex items-center gap-2.5'>
                            <BrandMark className='size-8' />
                            <span className='font-heading text-lg font-bold tracking-tight'>
                                HealthPath<span className='text-primary'>Core</span>
                            </span>
                        </div>

                        <p className='mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground'>
                            An independent health knowledge library. We turn published research and guidance
                            from named health bodies into clear, practical explanations, and correct them as
                            the evidence moves.
                        </p>
                    </div>

                    <div>
                        <h3 className='text-sm font-semibold text-foreground'>Topics</h3>

                        <ul className='mt-4 space-y-2.5'>
                            {HUBS.map(hub => (
                                <li key={hub.slug}>
                                    <Link
                                        href={`/${hub.slug}`}
                                        className='text-sm text-muted-foreground transition-colors hover:text-foreground'
                                    >
                                        {hub.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h3 className='text-sm font-semibold text-foreground'>HealthPathCore</h3>

                        <ul className='mt-4 space-y-2.5'>
                            <li>
                                <Link
                                    href='/guides'
                                    className='text-sm text-muted-foreground transition-colors hover:text-foreground'
                                >
                                    Complete guides
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href='/about'
                                    className='text-sm text-muted-foreground transition-colors hover:text-foreground'
                                >
                                    About &amp; editorial standards
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href='/methodology'
                                    className='text-sm text-muted-foreground transition-colors hover:text-foreground'
                                >
                                    How we research &amp; corrections
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href='/search'
                                    className='text-sm text-muted-foreground transition-colors hover:text-foreground'
                                >
                                    Search articles
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className='mt-10 flex items-start gap-3 rounded-lg border border-border bg-card p-4'>
                    <ShieldCheck className='mt-0.5 size-5 shrink-0 text-primary' />
                    <p className='text-xs leading-relaxed text-muted-foreground'>
                        The information on HealthPathCore is provided for general educational purposes only and is
                        not a substitute for professional medical advice, diagnosis, or treatment. Always seek the
                        guidance of a qualified health provider with any questions about a medical condition.
                    </p>
                </div>

                <div className='mt-8 flex flex-col items-center justify-between gap-3 border-t border-border pt-6 sm:flex-row'>
                    <p className='text-xs text-muted-foreground' suppressHydrationWarning>
                        © {year} HealthPathCore. All rights reserved.
                    </p>
                    <p className='text-xs text-muted-foreground'>
                        {siteConfig.url.replace('https://', '')}
                    </p>
                </div>
            </Container>
        </footer>
    )
}
